import React from "react";
import CrossAttachmentAssessment from "./CrossAttachmentAssessment";
import ZoomableImage from "../zoomablePic";
import { Box, Paper, Typography, Chip } from "@mui/material";
import { CheckCircle2, XCircle, Sparkles } from "lucide-react";

const ACCENT = "#f77f00";
const ACCENT_SOFT = "#fff4e5";
const BORDER = "#e6e9ef";
const TEXT_PRIMARY = "#1f2733";
const TEXT_SECONDARY = "#5b6472";

const AttachDisplay = ({ nextQuestion, question, selectedBox }) => {
  const subjective = question?.subjective_evaluation;
  const isAiEnabled = subjective?.enabled === true;

  // 🔥 FIX: Rubric ab sirf array par depend karega, enabled par nahi
  const hasRubric =
    Array.isArray(subjective?.rubric) && subjective.rubric.length > 0;

  // 🔹 Fallback: agar subjective_evaluation mein expectedAnswer nahi hai, toh correct_answers se lo
  const expectedAnswerText =
    subjective?.expectedAnswer ||
    question?.correct_answers
      ?.map((ans) => ans?.value || ans?.reason_for_answer?.explanation)
      .filter(Boolean)
      .join(", ");

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        p: 2,
        borderRadius: "14px",
        border: `1px solid ${BORDER}`,
        background: "#fff",
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: ACCENT,
          boxShadow: "0 4px 16px rgba(247,127,0,0.08)",
        },
      }}
    >
      {/* Header row: Q badge + title + marks */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "10px",
              background: `linear-gradient(135deg, ${ACCENT} 0%, #d35400 100%)`,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "13px",
              fontWeight: 700,
              flexShrink: 0,
              boxShadow: "0 3px 8px rgba(247,127,0,0.3)",
            }}
          >
            Q{nextQuestion + 1}
          </Box>

          <Typography
            sx={{
              fontSize: "14.5px",
              fontWeight: 600,
              color: TEXT_PRIMARY,
              lineHeight: 1.5,
              wordBreak: "break-word",
            }}
          >
            {question.title?.split("\n").map((line, index) => (
              <React.Fragment key={index}>
                {index !== 0 && <br />}
                <span>{line}</span>
              </React.Fragment>
            ))}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          {question?.correct_score ? (
            <Chip
              label={`${question.correct_score} Marks`}
              size="small"
              sx={{
                background: ACCENT_SOFT,
                color: ACCENT,
                fontWeight: 700,
                fontSize: "11.5px",
                border: `1px solid ${ACCENT}`,
              }}
            />
          ) : null}
          {question?.is_mandatory && (
            <Chip
              label="* Mandatory"
              size="small"
              sx={{
                background: "#fff0f0",
                color: "#dc3545",
                fontWeight: 600,
                fontSize: "10.5px",
                border: "1px solid #f5c2c7",
              }}
            />
          )}
        </Box>
      </Box>

      {/* Attachments */}
      {question?.attachments?.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
          {question?.attachments?.map((attachment, index) => {
            const fileName = attachment || "";
            const fileExtension = fileName.split(".").pop()?.toLowerCase();
            const isImage =
              fileExtension === "jpg" ||
              fileExtension === "jpeg" ||
              fileExtension === "png";

            return (
              <div key={index} style={{ maxWidth: "400px" }} className="w-auto">
                {isImage ? (
                  <ZoomableImage
                    src={attachment}
                    alt={`Attachment ${index + 1}`}
                  />
                ) : (
                  <CrossAttachmentAssessment attachment={attachment} />
                )}
              </div>
            );
          })}
        </Box>
      )}

      {/* 🔥 AI Auto-Evaluation Setup (Expected Answer sirf yahan dikhega) */}
      {subjective && selectedBox !== "teacher-evaluate" && (
        <Paper
          elevation={0}
          sx={{
            mt: 1,
            p: 1.5,
            borderRadius: "10px",
            background: ACCENT_SOFT,
            border: `1px solid ${ACCENT}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
              gap: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Sparkles size={13} color={ACCENT} />
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: ACCENT,
                  letterSpacing: "0.4px",
                }}
              >
                AI SETUP
              </Typography>
            </Box>

            {isAiEnabled ? (
              <Chip
                icon={<CheckCircle2 size={12} color="#fff" />}
                label="AI Evaluation: ON"
                size="small"
                sx={{
                  background: "#22c55e",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "10.5px",
                  height: 22,
                  "& .MuiChip-icon": { ml: "6px", color: "#fff" },
                }}
              />
            ) : (
              <Chip
                icon={<XCircle size={12} color="#fff" />}
                label="AI Evaluation: OFF"
                size="small"
                sx={{
                  background: "#9ca3af",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "10.5px",
                  height: 22,
                  "& .MuiChip-icon": { ml: "6px", color: "#fff" },
                }}
              />
            )}
          </Box>

          {/* 🔥 Expected Answer (BOLD & Prominent) */}
          {expectedAnswerText && (
            <Box sx={{ mb: 1 }}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: ACCENT,
                  letterSpacing: "0.4px",
                  mb: 0.5,
                  textTransform: "uppercase",
                }}
              >
                Expected Answer
              </Typography>
              <Typography
                sx={{
                  fontSize: "13.5px",
                  fontWeight: 600,
                  color: TEXT_PRIMARY,
                  lineHeight: 1.6,
                  opacity: isAiEnabled ? 1 : 0.75,
                }}
              >
                {expectedAnswerText}
              </Typography>
            </Box>
          )}

          {/* 🔥 Rubric — sirf tab jab data ho, AI ON/OFF se matlab nahi */}
          {hasRubric && (
            <Box sx={{ mt: 1.5 }}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: TEXT_SECONDARY,
                  mb: 0.5,
                  textTransform: "uppercase",
                }}
              >
                Marking Rubric
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {subjective.rubric.map((r, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: 0.75,
                      px: 1,
                      background: "#fff",
                      border: `1px solid ${BORDER}`,
                      borderRadius: "8px",
                      gap: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "12.5px",
                        color: TEXT_PRIMARY,
                        flex: 1,
                      }}
                    >
                      {r.criterion || "—"}
                    </Typography>
                    <Chip
                      label={`${r.maxMarks || 0} Marks`}
                      size="small"
                      sx={{
                        background: ACCENT_SOFT,
                        color: ACCENT,
                        fontWeight: 700,
                        fontSize: "10.5px",
                        height: 22,
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* 🔥 REMOVED: Warning jo "* Rubric is hidden..." dikha raha tha */}
        </Paper>
      )}
    </Paper>
  );
};

export default AttachDisplay;
